import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const textColor = isScrolled
    ? "text-foreground-900"
    : "text-background-50";
  const textMuted = isScrolled
    ? "text-foreground-700 hover:text-foreground-900"
    : "text-background-50/80 hover:text-background-50";
  const bgClass = isScrolled
    ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
    : "bg-transparent";
  const dropdownBg = isScrolled
    ? "bg-background-50 border-background-200/70"
    : "bg-background-50 border-background-200/70";
  const dropdownText = "text-foreground-700 hover:text-foreground-900 hover:bg-background-100";
  const catBtnBg = isScrolled
    ? "bg-background-100/80 text-foreground-700"
    : "bg-background-50/15 text-background-50/90 backdrop-blur-sm";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <span className={`text-lg md:text-xl font-heading font-semibold transition-colors duration-300 ${textColor}`}>
              Hoobuy <span className={isScrolled ? "text-primary-500" : "text-primary-400"}>Sheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textMuted}`}
            >
              Início
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${catBtnBg}`}
              >
                <span>Categorias</span>
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {isOpen && (
                <div className={`absolute top-full mt-2 left-0 w-60 border rounded-xl shadow-lg overflow-hidden z-50 max-h-80 overflow-y-auto ${dropdownBg}`}>
                  <div className="py-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.nameEn}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${dropdownText}`}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                          <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                        </div>
                        <span className="whitespace-nowrap">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/artigo/guia-completo-hoobuy-spreadsheet"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textMuted}`}
            >
              Guia de Compras
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? "text-background-50 bg-primary-500 hover:bg-primary-600"
                  : "text-foreground-900 bg-background-50 hover:bg-background-100"
              }`}
            >
              Hoobuy Sheet
            </a>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${textColor}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i
              className={`text-xl ${
                mobileMenuOpen ? "ri-close-line" : "ri-menu-line"
              }`}
            ></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-50/15 py-3 pb-5">
            <div className="flex flex-col gap-0.5">
              <Link
                to="/"
                className={`px-3 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${isScrolled ? "text-foreground-700 hover:text-foreground-900 hover:bg-background-100" : "text-background-50/80 hover:text-background-50 hover:bg-background-50/10"}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-home-4-line mr-2"></i>Início
              </Link>

              <button
                onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${isScrolled ? "text-foreground-700 hover:text-foreground-900 hover:bg-background-100" : "text-background-50/80 hover:text-background-50 hover:bg-background-50/10"}`}
              >
                <span><i className="ri-grid-line mr-2"></i>Categorias</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatsOpen ? "rotate-180" : ""}`}></i>
              </button>

              {mobileCatsOpen && (
                <div className="ml-3 border-l-2 border-background-200/30 pl-2 mt-1 mb-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.nameEn}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className={`flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors cursor-pointer ${isScrolled ? "text-foreground-600 hover:text-foreground-900 hover:bg-background-100" : "text-background-50/70 hover:text-background-50 hover:bg-background-50/10"}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="w-6 h-6 rounded overflow-hidden flex-shrink-0 bg-background-100">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <Link
                to="/artigo/guia-completo-hoobuy-spreadsheet"
                className={`px-3 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${isScrolled ? "text-foreground-700 hover:text-foreground-900 hover:bg-background-100" : "text-background-50/80 hover:text-background-50 hover:bg-background-50/10"}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-guide-line mr-2"></i>Guia de Compras
              </Link>

              <div className="border-t border-background-50/15 my-2"></div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="mx-3 mt-1 px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full text-center transition-all cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Hoobuy Sheet
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}